import {
	AfterContentChecked,
	AfterViewInit,
	Component,
	HostListener,
	Injectable,
	Input,
	OnDestroy,
	OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import {
	AudioElementWithSinkId,
	GspDeviceApi,
	GspDeviceApiProvider,
	MessplatzService
} from '@app/services/messplatz/messplatz.service';
import {
	cast,
	IQuestionnaireGroup,
	IQuestionnaireOverviewItem,
	Messgeraete,
} from '@interfaces/interfaces';
import { BlockQuestionItem, RangeQuestion, SpecialQuestionItem, SpecialQuestionType } from '@interfaces/questions';
import { WorkflowItemKeyDirective } from '@workflow/directives/workflow-item-key/workflow-item-key.directive';
import { Subscription } from 'rxjs';
import { WizardPage } from '@workflow/components/navigator/wizard-view/interfaces';
import { AbstractSpecialQuestionComponent, ISpecialQuestion, ISpecialQuestionComponent } from '../special-question.service';
import { DeveloperService } from '@app/services/developer/developer.service';
import { isNil } from 'lodash';
import { DigitalerHoerverlustEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/digitaler-hoerverlust/digitaler-hoerverlust-epilog.component';
import { Frequenz } from '../../../../../../../../../projects/bfs/build-scripts/lib/audio-referenz-data';

enum Frequenz_volume {
	Hz512_volume = 'Hz512_volume',
	Hz1024_volume = 'Hz1024_volume',
	Hz2048_volume = 'Hz2048_volume',
	Hz4096_volume = 'Hz4096_volume',
}

export interface DigitalerHoerverlustResults {
	rechts: {
		[key in Frequenz | Frequenz_volume]?: number;
	} & { fehlwert?: boolean };
	links: {
		[key in Frequenz | Frequenz_volume]?: number;
	} & { fehlwert?: boolean };
	soundDevice: Messgeraete;
	zusammenfassung?: {
	links: {
		transformed: {
			Hz2048_missingValue: number;
			Hz4096_missingValue: number;
		};
	};
	rechts: {
		transformed: {
			Hz2048_missingValue: number;
			Hz4096_missingValue: number;
		};
	};
	}
}

const LINKS_TRANSFORMED: IQuestionnaireOverviewItem[] = [
	{
		label: 'Hörverlust links 2048 Hz',
		key: 'hoerverlust.zusammenfassung.links.transformed.Hz2048',
		unit: '%',
		template: '{{value | decimal: \'1.1-1\'}} %',
		showOnlyMissingValue: true,
	}, {
		label: 'Hörverlust links 4096 Hz',
		key: 'hoerverlust.zusammenfassung.links.transformed.Hz4096',
		unit: '%',
		template: '{{value | decimal: \'1.1-1\'}} %',
		showOnlyMissingValue: true,
	},
];
const RECHTS_TRANSFORMED: IQuestionnaireOverviewItem[] = [
	{
		label: 'Hörverlust rechts 2048 Hz',
		key: 'hoerverlust.zusammenfassung.rechts.transformed.Hz2048',
		unit: '%',
		template: '{{value | decimal: \'1.1-1\'}} %',
		showOnlyMissingValue: true,
	}, {
		label: 'Hörverlust rechts 4096 Hz',
		key: 'hoerverlust.zusammenfassung.rechts.transformed.Hz4096',
		unit: '%',
		template: '{{value | decimal: \'1.1-1\'}} %',
		showOnlyMissingValue: true,
	},
];

@Injectable()
export class DigitalerHoerverlust implements ISpecialQuestion {
	public readonly component = DigitalerHoerverlustComponent;
	public readonly specialQuestionType: SpecialQuestionType = 'DigitalerHoerverlust';
	public readonly requiredDevices = [Messgeraete.CalibratedHeadphones];

	title = 'Hörverlust';

	public readonly defaultGroupConfiguration: IQuestionnaireGroup = {
		title: 'Hörverlust',
		preface: ['Bitte vermeiden Sie während des Hörtestes sämtliche Störgeräusche ' +
		'(Fenster und Türen schließen, alle nicht benötigten Geräte ausschalten!).' +
		' Der Proband sollte eine entspannte Sitzposition einnehmen.' +
		' Eine kurze Einführung vor Beginn: Es werden über den Kopfhörer vier unterschiedliche' +
		' Töne zuerst für das rechte und danach für das linke Ohr vorgespielt.' +
		' Die Töne sind zuerst nicht zu hören, werden aber lauter. Sobald die Töne das erste Mal' +
		' wahrgenommen werden, sind diese mit der Schaltfläche "Stop" bzw. dem Buzzer zu bestätigen.' +
		' Die Töne werden dann noch etwas lauter und danach wieder leiser, sobald der Ton nicht mehr' +
		' wahrzunehmen ist, ist dies wieder mit der Schaltfläche "Stop" bzw. dem Buzzer zu bestätigen.' +
		' Sie beginnen den Test durch Betätigen der “Test Starten” Schaltfläche auf der nächsten Seite.' +
		' Nachdem die Messung für ein Ohr erfolgt ist, beginnen Sie den Test für das andere Ohr durch erneutes Betätigen der "Test Starten" Schaltfläche'],
		items: [cast<BlockQuestionItem>({
			type: 'BlockQuestion',
			title: 'Hörverlust - rechtes Ohr',
			description: 'Bitte geben Sie die gemittelten dB-Werte der jeweiligen Frequenzen für das rechte Ohr an.',
			items: [{
				key: 'rechts.Hz512',
				label: '512 Hz',
			}, {
				key: 'rechts.Hz1024',
				label: '1024 Hz',
			}, {
				key: 'rechts.Hz2048',
				label: '2048 Hz',
			}, {
				key: 'rechts.Hz4096',
				label: '4096 Hz',
			}],
			default: cast<RangeQuestion>({
				type: 'range',
				rangeLow: 0,
				rangeHigh: 100,
				unit: 'dB',
				steps: 0.2,
			}),
			overview: [...RECHTS_TRANSFORMED],
			missingValueKey: 'rechts.fehlwert',
			isProcedure: true,
		}), cast<BlockQuestionItem>({
			type: 'BlockQuestion',
			title: 'Hörverlust - linkes Ohr',
			description: 'Bitte geben Sie die gemittelten dB-Werte der jeweiligen Frequenzen für das linke Ohr an.',
			items: [{
				key: 'links.Hz512',
				label: '512 Hz',
			}, {
				key: 'links.Hz1024',
				label: '1024 Hz',
			}, {
				key: 'links.Hz2048',
				label: '2048 Hz',
			}, {
				key: 'links.Hz4096',
				label: '4096 Hz',
			}],
			overview: [...LINKS_TRANSFORMED],
			default: cast<RangeQuestion>({
				type: 'range',
				rangeLow: 0,
				rangeHigh: 100,
				unit: 'dB',
				steps: 0.2,
			}),
			missingValueKey: 'links.fehlwert',
			isProcedure: true,
		})],
		startText: 'actions.goToTest',
	};

	public isCompleted(currentValue: DigitalerHoerverlustResults): boolean {
		if (!currentValue?.rechts || !currentValue?.links) {
			return false;
		}
		const frequenzArray = ['Hz512', 'Hz1024', 'Hz2048', 'Hz4096'];
		let completed = true;
		if (!currentValue.links.fehlwert) {
			completed = completed && frequenzArray.every((hz) =>
				!isNil(currentValue.links[hz]));
		}
		if (!currentValue.rechts.fehlwert) {
			completed = completed && frequenzArray.every((hz) =>
				!isNil(currentValue.rechts[hz]));
		}
		return completed;
	}

	buildWizardPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: specialQuestion,
			title: `${group.title} - rechtes Ohr`,
			subpage: 1,
			obligatoryUnskippableKeys: [specialQuestion.key],
			obligatoryAllKindOfKeys: [specialQuestion.key],
			disableNext: true,
			missingValueKey: 'rechts.fehlwert',
		}, {
			...group,
			question: specialQuestion,
			title: `${group.title} - linkes Ohr`,
			subpage: 2,
			obligatoryUnskippableKeys: [specialQuestion.key],
			obligatoryAllKindOfKeys: [specialQuestion.key],
			disableNext: true,
			missingValueKey: 'links.fehlwert',
		}];
	}

	buildEpilogPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: {
				type: 'epilog',
				title: group.title,
				centered: true,
			},
			subpage: 2,
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			disableNext: false,
			isProcedure: true,
			alignBottom: true,
			epilogComponent: DigitalerHoerverlustEpilogComponent,
			key: specialQuestion.key,
		}];
	}
}

@Component({
	selector: 'workflow-digitaler-hoerverlust',
	templateUrl: './digitaler-hoerverlust.component.html',
	styleUrls: ['./digitaler-hoerverlust.component.scss',
		'../../../../../../workflow/components/navigator/wizard-view/wizard-view.component.scss']
})
export class DigitalerHoerverlustComponent extends AbstractSpecialQuestionComponent implements OnInit, OnDestroy, AfterViewInit, AfterContentChecked, ISpecialQuestionComponent {

	/*
	Zeit in Millisekunden bis die Lautstärke von 0% auf 100% ansteigt.
	 */
	private readonly timeToFullVolume = 25000;

	private results: Partial<DigitalerHoerverlustResults>;
	private completed: boolean;
	private startTime = 0;
	private startDezibelFalling: number;
	public _fadeDirection: 'rising' | 'falling' = 'rising';
	private set fadeDirection(value: 'rising' | 'falling') {
		this._fadeDirection = value;
		this.updateShowTextFlags();
	}
	private risingVolumeLevel: number;
	private fallingVolumeLevel: number;
	private currentSoundDevice: Messgeraete;

	public isShowRisingVolumeText: boolean;
	public isShowFallingVolumeText: boolean;

	private intervalTimer;
	private timeoutTimer;
	public _delayFade: boolean;
	private set delayFade(value: boolean) {
		this._delayFade = value;
		this.updateShowTextFlags();
	}
	private gspDeviceApi: GspDeviceApi;

	private subscription = new Subscription();
	private audioSources: {
		rechts: {
			Hz512: HTMLAudioElement,
			Hz1024: HTMLAudioElement,
			Hz2048: HTMLAudioElement,
			Hz4096: HTMLAudioElement,
		},
		links: {
			Hz512: HTMLAudioElement,
			Hz1024: HTMLAudioElement,
			Hz2048: HTMLAudioElement,
			Hz4096: HTMLAudioElement,
		}
	};
	private side: 'links' | 'rechts';

	/**
	 * Dieses Element soll vor Start des Tests einen "Stummen sound" (volume 0%) absspielen, um sicherzustellen, dass der Kopfhörer nicht im standby ist.
	 */
	private silentAudioElement = new Audio('assets/sounds/512 R.mp3');

	private deviceAudioSources = {
		Audiometry_bd_DT_770_PRO_32__Sb_X_G1: {
			rechts: {
				Hz512: new Audio('assets/sounds/512 R.mp3'),
				Hz1024: new Audio('assets/sounds/1024 R.mp3'),
				Hz2048: new Audio('assets/sounds/2048 R.mp3'),
				Hz4096: new Audio('assets/sounds/4096 R.mp3'),
			},
			links: {
				Hz512: new Audio('assets/sounds/512 L.mp3'),
				Hz1024: new Audio('assets/sounds/1024 L.mp3'),
				Hz2048: new Audio('assets/sounds/2048 L.mp3'),
				Hz4096: new Audio('assets/sounds/4096 L.mp3'),
			}
		},
		Audiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB: {
			rechts: {
				Hz512: new Audio('assets/sounds/512 R.mp3'),
				Hz1024: new Audio('assets/sounds/1024 R.mp3'),
				Hz2048: new Audio('assets/sounds/2048 R.mp3'),
				Hz4096: new Audio('assets/sounds/4096 R.mp3'),
			},
			links: {
				Hz512: new Audio('assets/sounds/512 L.mp3'),
				Hz1024: new Audio('assets/sounds/1024 L.mp3'),
				Hz2048: new Audio('assets/sounds/2048 L.mp3'),
				Hz4096: new Audio('assets/sounds/4096 L.mp3'),
			}
		},
	};
	private trackList: {hz: string, side: 'links'|'rechts'}[] = [];
	public currentTrack: {index: number, hz: string, side: 'links'|'rechts', audioObj: HTMLAudioElement};
	public templateValVolume = 0;
	public isDevMode: boolean;

	public _testStarted = false;
	private set testStarted(value: boolean) {
		this._testStarted = value;
		this.updateShowTextFlags();
	}

	public buttonText = 'actions.starttest';

	@Input() page: number;

	constructor(
		private workflowItemKey: WorkflowItemKeyDirective,
		router: Router,
		private messplatzService: MessplatzService,
		private developerService: DeveloperService,
	) {
		super(router);
		const gspDeviceApiProvider = window as unknown as GspDeviceApiProvider;
		this.gspDeviceApi = gspDeviceApiProvider?.GspDeviceApi;
	}

	ngOnInit(): void {
		this.subscription.add(this.workflowItemKey.propertyFull$.subscribe(data => {
			if (this.finishedAndLocked) {
				this.results = data as DigitalerHoerverlustResults;
				this.goToEpilog();
			}
		}));

		this.subscription.add(this.developerService.isDevMode$.subscribe(isDevMode => this.isDevMode = isDevMode));

		this.side = this.page === 1 ? 'rechts' : 'links';

		const soundDevices = this.messplatzService.getConnectedSoundDevices();
		if (soundDevices?.length > 0) {
			switch (soundDevices[0]) {
				case Messgeraete.Audiometry_bd_DT_770_PRO_32__Sb_X_G1: this.audioSources = this.deviceAudioSources.Audiometry_bd_DT_770_PRO_32__Sb_X_G1; break;
				case Messgeraete.Audiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB: this.audioSources = this.deviceAudioSources.Audiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB; break;
			}
			this.currentSoundDevice = soundDevices[0];
			if (this.results) {
				this.results.soundDevice = this.currentSoundDevice;
			}
		}

		this.subscription.add(this.workflowItemKey.property$.subscribe(data => {
			this.results = data as DigitalerHoerverlustResults || {};
			this.results.soundDevice = this.currentSoundDevice;
			if (!this.results.rechts || (! this._testStarted && this.side === 'rechts')) {
				this.results.rechts = {};
			}
			if (!this.results.links || (!this._testStarted && this.side === 'links')) {
				this.results.links = {};
			}
		}));


		this.loadAudio();
		this.loadTrackList();

		(async () => {

			await this.setSinkIds();

			this.silentAudioElement.volume = 0.0000000000001;
			this.silentAudioElement.loop = true;
			await this.silentAudioElement.play();

		})().catch(error => console.error(error));
	}

	ngOnDestroy(): void {
		this.resetTimer();
		this.resetAudio();
		this.subscription.unsubscribe();
	}

	ngAfterViewInit(): void {
		this.resetTimer();
	}

	ngAfterContentChecked(): void {
		this.templateValVolume = this.currentTrack.audioObj.volume || 0;
	}

	async dispatchVitaReact($event: Event): Promise<void> {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	@HostListener('window:keydown', ['$event'])
	keyPress($event: Event): void {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	@HostListener('window:vitareact-press', ['$event'])
	vitaReactPress(): void {
		this.hitButton();
	}



	private loadTrackList(): void {
		this.trackList = [];
		for (const freq of Object.keys(this.audioSources[this.side])) {
			this.trackList.push({
				hz: freq,
				side: this.side as 'links' | 'rechts',
			});
		}

		this.currentTrack = {
			index: 0,
			hz: this.trackList[0].hz,
			side: this.trackList[0].side,
			audioObj: this.audioSources[this.trackList[0].side][this.trackList[0].hz],
		};
	}

	private async setSinkIds() {
		await this.messplatzService.setCalibratedHeadphonesSinkId(this.audioSources.rechts.Hz512 as AudioElementWithSinkId);
		await this.messplatzService.setCalibratedHeadphonesSinkId(this.audioSources.rechts.Hz1024 as AudioElementWithSinkId);
		await this.messplatzService.setCalibratedHeadphonesSinkId(this.audioSources.rechts.Hz2048 as AudioElementWithSinkId);
		await this.messplatzService.setCalibratedHeadphonesSinkId(this.audioSources.rechts.Hz4096 as AudioElementWithSinkId);
		await this.messplatzService.setCalibratedHeadphonesSinkId(this.audioSources.links.Hz512 as AudioElementWithSinkId);
		await this.messplatzService.setCalibratedHeadphonesSinkId(this.audioSources.links.Hz1024 as AudioElementWithSinkId);
		await this.messplatzService.setCalibratedHeadphonesSinkId(this.audioSources.links.Hz2048 as AudioElementWithSinkId);
		await this.messplatzService.setCalibratedHeadphonesSinkId(this.audioSources.links.Hz4096 as AudioElementWithSinkId);
		await this.messplatzService.setCalibratedHeadphonesSinkId(this.silentAudioElement as AudioElementWithSinkId);
	}

	private resetAudio() {
		if (!this.silentAudioElement.paused) {
			this.silentAudioElement.pause();
		}
		if (!this.currentTrack.audioObj.paused) {
			this.currentTrack.audioObj.pause();
		}
		if (this.currentTrack.audioObj.playbackRate > 0) {
			this.currentTrack.audioObj.currentTime = 0;
		}
	}

	private checkProgressAndMove(): void {
		this.completed = ['Hz512_volume','Hz1024_volume','Hz2048_volume','Hz4096_volume'].every((hz) =>
			this.results?.[this.side] && !isNil(this.results[this.side][hz]));
		this.valid.emit(this.completed);

		if (this.completed) {
			if (this.page === 1) {
				this.goToNext();
				return;
			} else {
				this.goToEpilog();
				return;
			}
		}

		if (!this.currentTrack.hz) {
			throw new Error(`Empty track with index ${this.currentTrack.index}`);
		}

		this.playSoundFade(this._fadeDirection);
	}

	private updateResults(): void {
		this.workflowItemKey.setProperty(this.results);
	}

	private loadAudio(): void {
		this.silentAudioElement.load();
		if (this.page === 1) {
			for (const linksKey in this.audioSources.links) {
				this.audioSources.links[linksKey].load();
			}
		}
		if (this.page === 2) {
			for (const rechtsKey in this.audioSources.rechts) {
				this.audioSources.rechts[rechtsKey].load();
			}
		}
	}

	private setNextAudioTrack(): void {
		const tempTrack = this.currentTrack;
		tempTrack.index++;

		if (!this.trackList[tempTrack.index]) {
			this.currentTrack.hz = null;
			return;
		}

		this.currentTrack = {
			index: tempTrack.index,
			hz: this.trackList[tempTrack.index].hz,
			side: this.trackList[tempTrack.index].side,
			audioObj: this.audioSources[this.trackList[tempTrack.index].side][this.trackList[tempTrack.index].hz],
		};
	}

	private async resetSystemVolume(): Promise<void> {
		await this.gspDeviceApi.setSystemVolume(100);
	}

	private playSoundFade(soundFade: 'rising' | 'falling'): void {
		let keepSoundLevelMS = 9999999;
		if (this.currentTrack.audioObj.paused) {
			if (this.gspDeviceApi) {
				this.resetSystemVolume().catch(console.error);
			}
			this.currentTrack.audioObj.play().finally(() => keepSoundLevelMS = 1200).catch(console.error);
			this.currentTrack.audioObj.loop = true;
		}
		else {
			keepSoundLevelMS = 1200;
		}
		this.currentTrack.audioObj.volume = soundFade === 'rising' ? 0 : Math.min(1, this.currentTrack.audioObj.volume);

		this.intervalTimer = setInterval(() => {
			if (keepSoundLevelMS > 0) {
				keepSoundLevelMS -= 10;
				this.startTime = performance.now();
				return;
			}
			const deltaTime = performance.now() - this.startTime;
			const currentDezibel = (deltaTime / this.timeToFullVolume) * 100;
			if (soundFade === 'rising') {
				this.startDezibelFalling = currentDezibel;
			}
			const dezibelValue = soundFade === 'rising' ? currentDezibel : this.startDezibelFalling - currentDezibel;
			let volume = Math.pow(10,(dezibelValue - 100)/20);
			//Can't be lower than 0 can't be higher than 1
			volume = Math.min(Math.max(volume, 0), 1);
			this.currentTrack.audioObj.volume = volume;

			if (!this._delayFade) {
				if (soundFade === 'rising') {
					this.risingVolumeLevel = this.currentTrack.audioObj.volume;
				} else {
					this.fallingVolumeLevel = this.currentTrack.audioObj.volume;
				}
			}
		}, 10);
	}

	private resetTimer(): void {
		this.startTime = 0;
		clearInterval(this.intervalTimer);
		clearTimeout(this.timeoutTimer);
	}

	private parseAudioTrackToResult() {
		const audioLevel = (this.risingVolumeLevel + this.fallingVolumeLevel)/2;
		this.results[this.currentTrack.side][`${this.currentTrack.hz}_volume`] = isNaN(audioLevel) || isNil(audioLevel) ? this.currentTrack.audioObj.volume : audioLevel;
	}

	hitButton(): void {
		if (!this._testStarted) {
			this.silentAudioElement.pause();
			this.testStarted = true;
			this.playSoundFade(this._fadeDirection);
			this.buttonText = 'actions.stop';
		} else {
			this.delayFade = true;
			if (!this.finishedAndLocked && !this.currentTrack.audioObj.paused && this.startTime) {
				this.timeoutTimer = setTimeout(() => {
					this.delayFade = false;
					this.resetAudio();
					this.resetTimer();
					if (this._fadeDirection === 'falling') {
						this.parseAudioTrackToResult();
						this.updateResults();
						this.setNextAudioTrack();
					}
					this.fadeDirection = this._fadeDirection === 'rising' ? 'falling' : 'rising';
					this.checkProgressAndMove();
				}, 500);
			}
		}
	}

	/**
	 * Diese beiden Methoden geben an, ob eine entsprechende Eingabeaufforderung "Bitte klicken, wenn Sie den Ton hören/nicht mehr hören"
	 * ausgegeben werden soll.
	 */
	updateShowTextFlags(): void {
		this.isShowRisingVolumeText = this._testStarted && this._fadeDirection === 'rising' && !this._delayFade;
		this.isShowFallingVolumeText = this._testStarted && this._fadeDirection === 'falling' && !this._delayFade;
	}
}
